import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Download, Loader2, CheckCircle2, AlertCircle, Edit3, X, RefreshCw, AlertOctagon } from "lucide-react";
import api, { API_BASE, fmtErr } from "../api";
import {
  ClaimedBadge, UnclaimedBadge, DemoBadge, GovListedBadge,
  CorridorVerifiedBadge, LegacyVerifiedBadge, ReviewCandidateBadge,
  HelpTip, ActionTooltip, AdvancedToolsSection, AdvancedRow,
  DUPLICATE_CANDIDATE_SLUGS,
} from "./adminUiHelpers";

/**
 * Admin · Suppliers — /admin/suppliers
 *
 * Responsibilities:
 *  - Counts: total / claimed / unclaimed / claim_pending / founding
 *  - CSV upload (POST /api/admin/import-immex-csv)
 *  - Import history table
 *  - Supplier list with inline-edit drawer
 *  - CSV export button (GET /api/admin/suppliers/export.csv)
 */
export default function AdminSuppliers() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        api.get("/admin/suppliers"),
        api.get("/admin/imports"),
      ]);
      setData(d.data);
      setHistory(h.data.items || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    (async () => {
      await load();
    })();
  }, []);

  async function handleUpload(e) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post(
        "/admin/import-immex-csv",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportResult(res);
      await load();
    } catch (err) {
      setImportResult({ error: fmtErr(err.response?.data?.detail || err.message) });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleExport() {
    // Direct browser download — credentials are sent via cookie (withCredentials)
    const a = document.createElement("a");
    a.href = `${API_BASE}/admin/suppliers/export.csv`;
    a.download = "corridor-suppliers-export.csv";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const counts = data.counts || {};
  const items = (data.items || []).filter((s) => {
    if (statusFilter && s.profile_status !== statusFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (s.company_name || "").toLowerCase().includes(q) ||
      (s.immex_number || "").toLowerCase().includes(q) ||
      (s.city || "").toLowerCase().includes(q) ||
      (s.industry || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="container-cd py-12" data-testid="admin-suppliers-page">
      <div className="overline">ADMIN · SUPPLIER DIRECTORY</div>
      <div className="flex flex-wrap items-end justify-between gap-3 mt-2">
        <h1 className="font-display text-4xl md:text-5xl leading-[0.95]">
          Suppliers
        </h1>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/seo"
            className="btn btn-ghost btn-sm inline-flex items-center gap-1"
            data-testid="admin-suppliers-seo-link"
          >
            SEO Dashboard
          </Link>
          <button
            onClick={load}
            className="btn btn-ghost btn-sm inline-flex items-center gap-1"
            data-testid="admin-suppliers-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={handleExport}
            className="btn btn-dark btn-sm inline-flex items-center gap-1"
            data-testid="admin-suppliers-export"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Counts row — plain-English, with tooltips */}
      <section
        className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3"
        data-testid="admin-suppliers-counts"
      >
        <Count
          label="TOTAL"
          value={counts.total ?? 0}
          tip="Every profile row: unclaimed IMMEX registry rows, in-progress claims, real claims, and founding members."
          testid="count-total"
        />
        <Count
          label="CLAIMED"
          value={counts.claimed ?? 0}
          accent="var(--green)"
          tip="Real user accounts that have completed the claim flow."
          testid="count-claimed"
        />
        <Count
          label="PENDING"
          value={counts.claim_pending ?? 0}
          accent="#ea580c"
          tip="Claim requests waiting for admin approval."
          testid="count-pending"
        />
        <Count
          label="UNCLAIMED"
          value={counts.unclaimed ?? 0}
          accent="var(--ink-2)"
          tip="No user has claimed ownership yet. Includes government-listed IMMEX registry imports."
          testid="count-unclaimed"
        />
        <Count
          label="FOUNDING"
          value={counts.verified_founding_member ?? 0}
          accent="var(--orange)"
          tip="Founding member plants — paid tier, special badge on public directory."
          testid="count-founding"
        />
        <Count
          label="REVIEW CANDIDATE"
          value={items.filter((r) => DUPLICATE_CANDIDATE_SLUGS.has((r.slug || "").toLowerCase())).length}
          accent="#dc2626"
          tip="Duplicate-candidate plants flagged in the audit report. Kept unchanged — no merge, no delete, no verification will happen automatically."
          testid="count-review-candidate"
        />
      </section>

      {/* Truth-labels reminder */}
      <section
        className="mt-4 cd-card"
        style={{ borderLeft: "4px solid #3b82f6", padding: 14, background: "rgba(59,130,246,0.03)" }}
        data-testid="admin-suppliers-truth-note"
      >
        <div className="overline" style={{ color: "#1e40af" }}>
          <AlertCircle size={11} className="inline mr-2 -mt-1" /> TRUTH LABELS
        </div>
        <p className="text-sm mt-1" style={{ color: "var(--ink)" }}>
          <strong>Government-listed IMMEX</strong> means the record came from the
          Mexican government's registry. That is <strong>NOT</strong> the same
          as <strong>Corridor Verified</strong> — Corridor Verified requires
          plant-level evidence reviewed here. Do not verify a plant based on
          registry presence alone.
        </p>
      </section>


      {/* Legacy CSV upload — clearly under Advanced / UNSAFE.
          The endpoint (POST /admin/import-immex-csv) has historically
          hardcoded verified=true on inserted rows, which is unsafe.
          Kept available but visibly labelled. */}
      <AdvancedToolsSection>
        <AdvancedRow
          title="Import IMMEX CSV (legacy CSV importer)"
          tooltipProps={{
            what: "Uploads a CSV of IMMEX registry rows. Legacy importer — historically hardcoded 'verified=true' on inserted rows. Do NOT use against the audited Juárez 636 CSV — use the receipt-gated corridor upsert instead. Required columns: company_name, immex_number. De-dupes by immex_number.",
            changesData: true, sendsEmail: false, sendsIndexNow: true,
            undoable: false, backupRequired: true,
            testid: "tip-legacy-csv-import",
          }}
          action={(
            <form onSubmit={handleUpload} className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="font-mono text-xs"
                data-testid="admin-suppliers-file-input"
                required
              />
              <button
                type="submit"
                disabled={importing}
                className="btn btn-dark btn-sm inline-flex items-center gap-2"
                data-testid="admin-suppliers-upload-btn"
              >
                {importing ? (
                  <><Loader2 size={14} className="animate-spin" /> Importing…</>
                ) : (
                  <><Upload size={14} /> Upload · UNSAFE</>
                )}
              </button>
            </form>
          )}
          resultLine={importResult
            ? (importResult.error
                ? <span style={{ color: "var(--red)" }}><AlertCircle size={11} className="inline -mt-0.5" /> {importResult.error}</span>
                : <span style={{ color: "var(--green)" }}><CheckCircle2 size={11} className="inline -mt-0.5" /> Imported {importResult.imported} · Skipped {importResult.skipped}{importResult.error_count > 0 ? ` · ${importResult.error_count} errors` : ""}{importResult.indexnow_submitted > 0 ? ` · IndexNow pinged ${importResult.indexnow_submitted} URLs` : ""}</span>)
            : null}
        />
      </AdvancedToolsSection>


      {/* Import history */}
      <section className="mt-8" data-testid="admin-import-history-section">
        <div className="overline">IMPORT HISTORY</div>
        {history.length === 0 ? (
          <p className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            No imports yet.
          </p>
        ) : (
          <div className="mt-3 cd-card p-0 overflow-x-auto">
            <table className="cd-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>By</th>
                  <th>File</th>
                  <th className="text-right">Imported</th>
                  <th className="text-right">Skipped</th>
                  <th className="text-right">Errors</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr
                    key={h.import_id}
                    data-testid={`import-row-${h.import_id}`}
                  >
                    <td className="font-mono text-xs">{h.created_at}</td>
                    <td className="font-mono text-xs">{h.performed_by || "—"}</td>
                    <td className="font-mono text-xs">{h.filename || "—"}</td>
                    <td
                      className="font-mono text-xs text-right"
                      style={{ color: "var(--green)" }}
                    >
                      {h.imported}
                    </td>
                    <td
                      className="font-mono text-xs text-right"
                      style={{ color: "var(--ink-2)" }}
                    >
                      {h.skipped}
                    </td>
                    <td
                      className="font-mono text-xs text-right"
                      style={{ color: h.error_count ? "var(--red)" : "var(--ink-2)" }}
                    >
                      {h.error_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Supplier list */}
      <section className="mt-10" data-testid="admin-supplier-list-section">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="overline">ALL PROFILES · {items.length}</div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="cd-input font-mono text-xs"
              style={{ maxWidth: 220, height: 36 }}
              data-testid="admin-supplier-status-filter"
            >
              <option value="">All statuses</option>
              <option value="unclaimed">Unclaimed</option>
              <option value="claim_pending">Claim pending</option>
              <option value="claimed">Claimed</option>
              <option value="verified_founding_member">Founding Member</option>
            </select>
            <input
              type="text"
              placeholder="Search name / IMMEX / city…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="cd-input font-mono text-xs"
              style={{ maxWidth: 280, height: 36 }}
              data-testid="admin-supplier-filter"
            />
          </div>
        </div>

        {loading ? (
          <div className="mt-6 overline">LOADING…</div>
        ) : (
          <div className="mt-3 cd-card p-0 overflow-x-auto">
            <table className="cd-table">
              <thead>
                <tr>
                  <th>Company / Legal Entity</th>
                  <th>City</th>
                  <th>IMMEX Ref</th>
                  <th>Truth badges</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const isDup = DUPLICATE_CANDIDATE_SLUGS.has((s.slug || "").toLowerCase());
                  const isImmex = (s.slug || "").startsWith("immex-");
                  return (
                    <tr
                      key={s.profile_id}
                      data-testid={`admin-supplier-row-${s.slug}`}
                      style={isDup ? { background: "#fff7ed" } : {}}
                    >
                      <td className="font-semibold" style={{ maxWidth: 260 }}>
                        <Link
                          to={`/m/${s.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                          title="Open the public plant profile in a new tab. Read-only."
                        >
                          {s.display_name || s.legal_entity || s.company_name}
                        </Link>
                        <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                          {s.slug || s.profile_id}
                          {s.plant_sequence_label ? <span> · {s.plant_sequence_label}</span> : null}
                        </div>
                      </td>
                      <td className="font-mono text-xs">{s.city || "—"}</td>
                      <td className="font-mono text-xs">
                        {s.immex_program_reference || s.immex_number || "—"}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {isImmex && !s.corridor_verified ? <GovListedBadge /> : null}
                          {s.corridor_verified ? <CorridorVerifiedBadge /> : null}
                          {s.verified && !s.corridor_verified ? <LegacyVerifiedBadge /> : null}
                          {s.claimed ? <ClaimedBadge /> : (isImmex ? <UnclaimedBadge /> : null)}
                          {s.is_demo ? <DemoBadge /> : null}
                          {isDup ? <ReviewCandidateBadge /> : null}
                        </div>
                      </td>
                      <td>
                        <StatusChip status={s.profile_status} />
                      </td>
                      <td className="text-right">
                        <button
                          onClick={() => setEditing(s)}
                          className="btn btn-ghost btn-sm inline-flex items-center gap-1"
                          data-testid={`admin-supplier-edit-${s.slug}`}
                          title="Open the full editor drawer for this record. Changes to Verified / Published are legacy operations — see the tooltip inside the drawer before saving."
                        >
                          <Edit3 size={12} /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {items.length === 0 ? (
              <div className="p-8 text-center font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                No suppliers match the current filter.
              </div>
            ) : null}
          </div>
        )}
      </section>

      {editing ? (
        <EditDrawer
          supplier={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function Count({ label, value, accent, testid, tip }) {
  return (
    <div className="cd-card p-4" data-testid={testid}>
      <div className="overline flex items-center" style={{ color: accent || "var(--ink-2)" }}>
        <span>{label}</span>
        {tip ? <HelpTip text={tip} testid={`${testid}-tip`} /> : null}
      </div>
      <div className="font-display text-3xl mt-2 leading-none">{value}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    verified_founding_member: { bg: "var(--orange)", color: "white", label: "FOUNDING" },
    claimed: { bg: "var(--green)", color: "white", label: "CLAIMED" },
    claim_pending: { bg: "#ea580c", color: "white", label: "PENDING" },
    unclaimed: { bg: "var(--bg)", color: "var(--ink-2)", label: "UNCLAIMED", border: "1px solid var(--line)" },
  };
  const cfg = map[status] || map.unclaimed;
  return (
    <span
      className="cd-chip"
      style={{ background: cfg.bg, color: cfg.color, border: cfg.border, fontSize: 10 }}
      data-testid={`status-chip-${status}`}
    >
      {cfg.label}
    </span>
  );
}

function EditDrawer({ supplier, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: supplier.company_name || "",
    industry: supplier.industry || "",
    city: supplier.city || "",
    state: supplier.state || "",
    country: supplier.country || "",
    immex_number: supplier.immex_number || "",
    immex_status: supplier.immex_status || "",
    program_type: supplier.program_type || "",
    contact_email: supplier.contact_email || "",
    contact_phone: supplier.contact_phone || "",
    street_address: supplier.street_address || "",
    zip_code: supplier.zip_code || "",
    profile_status: supplier.profile_status || "unclaimed",
    published: !!supplier.published,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  async function save() {
    setSaving(true);
    setError("");
    try {
      // The PATCH endpoint expects industries as an array; pass through.
      const payload = {
        ...form,
        industries: form.industry ? [form.industry] : [],
      };
      delete payload.industry;
      await api.patch(`/admin/suppliers/${supplier.profile_id}`, payload);
      onSaved();
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 60,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
      data-testid="admin-edit-overlay"
    >
      <div
        className="cd-card"
        style={{
          background: "white",
          maxWidth: 520,
          width: "100%",
          padding: 24,
          overflowY: "auto",
          height: "100vh",
          borderRadius: 0,
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-edit-drawer"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline">EDIT SUPPLIER</div>
            <h2 className="font-display text-2xl mt-1 leading-tight">
              {supplier.company_name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost"
            style={{ padding: 6, minHeight: 0 }}
            data-testid="admin-edit-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-6 space-y-3">
          <Input label="Company name" value={form.company_name} onChange={(v) => update("company_name", v)} testid="edit-name" />
          <Input label="Industry" value={form.industry} onChange={(v) => update("industry", v)} testid="edit-industry" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="City" value={form.city} onChange={(v) => update("city", v)} testid="edit-city" />
            <Input label="State" value={form.state} onChange={(v) => update("state", v)} testid="edit-state" />
          </div>
          <Input label="Country" value={form.country} onChange={(v) => update("country", v)} testid="edit-country" />
          <Input label="IMMEX number" value={form.immex_number} onChange={(v) => update("immex_number", v)} testid="edit-immex" />
          <Input label="IMMEX status" value={form.immex_status} onChange={(v) => update("immex_status", v)} testid="edit-immex-status" />
          <Input label="Program type" value={form.program_type} onChange={(v) => update("program_type", v)} testid="edit-program" />
          <Input label="Contact email" value={form.contact_email} onChange={(v) => update("contact_email", v)} testid="edit-email" />
          <Input label="Contact phone" value={form.contact_phone} onChange={(v) => update("contact_phone", v)} testid="edit-phone" />
          <Input label="Street address" value={form.street_address} onChange={(v) => update("street_address", v)} testid="edit-street" />
          <Input label="Zip code" value={form.zip_code} onChange={(v) => update("zip_code", v)} testid="edit-zip" />
          <div>
            <label className="overline">Profile status</label>
            <select
              value={form.profile_status}
              onChange={(e) => update("profile_status", e.target.value)}
              className="cd-input mt-2 font-mono text-xs"
              data-testid="edit-status"
            >
              <option value="unclaimed">Unclaimed</option>
              <option value="claim_pending">Claim pending</option>
              <option value="claimed">Claimed</option>
              <option value="verified_founding_member">Founding Member</option>
            </select>
          </div>
          <div className="pt-2">
            <div
              className="cd-card"
              style={{
                borderLeft: "3px solid #dc2626",
                background: "rgba(220,38,38,0.03)",
                padding: 10,
              }}
              data-testid="edit-legacy-flags-warning"
            >
              <div
                className="overline flex items-center"
                style={{ color: "#dc2626", marginBottom: 4 }}
              >
                <AlertOctagon size={11} className="inline mr-2 -mt-1" />
                LEGACY FLAGS · USE WITH CARE
                <ActionTooltip
                  what="Published controls public directory visibility. The legacy Verified checkbox has been removed — Corridor Verified now requires the evidence-based ceremony in the Compliance Vault, and the old one-click Verify is hard-blocked server-side."
                  changesData={true}
                  sendsEmail={false}
                  sendsIndexNow={false}
                  undoable={true}
                  backupRequired={false}
                  testid="tip-legacy-verify-publish"
                />
              </div>
              <div className="flex flex-wrap items-center gap-4 pt-1">
                <a
                  href={`/admin/vault?plant=${supplier.slug}`}
                  className="font-mono text-xs underline"
                  style={{ color: "var(--ink)" }}
                  data-testid="edit-verify-ceremony-link"
                >
                  Verify via evidence ceremony →
                </a>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => update("published", e.target.checked)}
                    data-testid="edit-published"
                  />
                  <span className="font-mono text-xs">Published</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div className="mt-4 text-sm" style={{ color: "var(--red)" }} data-testid="edit-error">
            {error}
          </div>
        ) : null}

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn btn-primary inline-flex items-center gap-2"
            data-testid="edit-save"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save"}
          </button>
          <button type="button" onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input
        type="text"
        className="cd-input mt-2"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
